import { AbstractControl, AsyncValidatorFn, FormArray, ValidatorFn } from '@angular/forms';
import { LorenzFormMixin } from './lorenz-form.mixin';
import { LorenzFormGroup } from './lorenz-form-group';
import { LorenzFormService } from './lorenz-form.service';
import { LorenzControlTypeEnum } from './enums/lorenz-control-type.enum';
import { setProperty } from '@lib/shared';
import { LorenzFormPayload } from './interfaces/lorenz-control-payload.interface';

export class LorenzFormList extends LorenzFormMixin(FormArray) {
	private static _lorenzFormService: LorenzFormService;

	// prettier-ignore
	constructor(groups: LorenzFormGroup[],
				validatorOrOpts?: ValidatorFn | ValidatorFn[] | null,
				asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null,
				payload?: LorenzFormPayload
	) {
		super(groups, validatorOrOpts, asyncValidator);

		this.payload = payload ?? {};

		// console.log('LorenzFormList', this, this.payload);

		// this.addItem(); // Each list has at least one item!
	}

	get lorenzFormService() {
		if (!LorenzFormList._lorenzFormService) {
			LorenzFormList._lorenzFormService = new LorenzFormService();
		}
		return LorenzFormList._lorenzFormService;
	}

	// Die LorenzFormList darf nur LorenzFormGroup Objekte enthalten!
	override push(control: AbstractControl): void {
		if (control instanceof LorenzFormGroup) {
			super.push(control);
		}
	}

	addItem(): LorenzFormGroup | null {
		const itemFn = this.payload?.listItemForm;
		if (itemFn instanceof Function) {
			const item = itemFn(this.payload?.listItemFormPayload?.options, this.payload?.listItemFormPayload?.functions, this);
			// item.reset();

			const idControl = item.get('id');
			if (idControl) {
				const idx = this.length + 1;
				idControl.patchValue(idx);
				setProperty(idControl.payload, 'originalValue', idx);
			}

			this.push(item);
			this.setListKeys();
			this.lorenzFormService.initValidations(item);
			this.lorenzFormService.applyDependencies(this.getRoot(), item);

			return item;
		}
		return null;
	}

	removeItem(lorenzFormGroup: LorenzFormGroup) {
		if (this.controls.length > 1) {
			const componentRef = lorenzFormGroup.payload.listItemComponentRef;

			if (componentRef && this.payload.listComponentRefs) {
				componentRef.destroy();
				const index = this.payload.listComponentRefs.indexOf(componentRef);
				if (index > -1) {
					this.payload.listComponentRefs.splice(index, 1);
				}
			}

			const index = this.controls.indexOf(lorenzFormGroup);
			if (index !== -1) {
				this.removeAt(index);
				this.setListKeys();
			}

			if (this.payload.listItemRemoveFn) {
				this.payload.listItemRemoveFn(null, this.getRoot(), this);
			}
		}
	}

	get items(): LorenzFormGroup[] {
		return this.lorenzFormService.convertChildGroupsToArray(this) ?? [];
	}

	override clear(): void {
		// console.log('CLEAR', this.items);
		while (this.length > 0) {
			this.removeAt(0);
		}
	}

	override patchValue(value: any): void {
		if (!value) {
			return;
		}

		// Überprüfe, ob der Wert ein Array ist
		if (!Array.isArray(value)) {
			console.error('Fehler: Der übergebene Wert ist kein Array:', value);
			return; // Bricht ab, falls der Wert kein Array ist
		}

		// Clear all contents and renew them all!
		this.clear();
		value.forEach(() => this.addItem());

		// Ensure all freshly created controls are enabled so incoming values are actually patched,
		// even if dependencies disabled them during addItem() before values exist.
		this.controls.forEach((control) => control.enable());

		// Ruft `patchValue` auf, wenn der Wert ein Array ist und die richtige Länge hat
		super.patchValue(value);
	}

	private setListKeys() {
		(this.controls as LorenzFormGroup[]).forEach((control: LorenzFormGroup, index: number) => {
			control.payload = {
				...control.payload,
				type: LorenzControlTypeEnum.LISTITEM,
				key: index.toString()
			};
		});
	}
}
